import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
    Banner, TextField, IndexTable, Card, IndexFilters, useSetIndexFiltersMode, useIndexResourceState, Text, ChoiceList,
    RangeSlider, Avatar, ResourceList, Box, Badge, useBreakpoints, DataTable, Scrollable, Button, Popover, Select, Icon,
    DatePicker, InlineStack, BlockStack, InlineGrid, OptionList, Tooltip, Layout, Page
} from '@shopify/polaris';
import { ArrowUpIcon, ArrowDownIcon, CalendarIcon, ArrowRightIcon, ExportIcon, PersonIcon, PersonFilledIcon, TaxIcon, MoneyIcon, AlertCircleIcon, ReceiptIcon, TransactionIcon, RewardIcon, WalletIcon, DiscountIcon, ClockIcon, InfoIcon, WorkIcon, CashDollarIcon, ChartVerticalIcon, ListBulletedIcon } from '@shopify/polaris-icons';
import { ColumnHeading, IconDisc, SURFACE } from '../ui/CardSurface';
import SegmentedTabs from '../SegmentedTabs';
import useAxiosClient from '../axiosClient';
import { SparkLineChart } from '@shopify/polaris-viz';
import '@shopify/polaris-viz/build/esm/styles.css';
import { StatBox } from "../polaris-viz";
import { useTranslation } from 'react-i18next';

const data = {
    series: [
        {
            data: [
                { value: 0, key: '2023-01-01' },
                { value: 5, key: '2023-01-02' },
                { value: 1, key: '2023-01-03' },
            ],
        },
    ],
};

const tableData = [
    { id: '1', name: 'Galaxy', position: 'Staff', salary: '$0.00', totalShifts: 0, totalHours: 0, breakTimes: 0, totalEarnings: '$10' },
    { id: '2', name: 'Smith', position: 'Manager', salary: '$0.00', totalShifts: 0, totalHours: 0, breakTimes: 0, totalEarnings: '$10' },
    { id: '3', name: 'Jack', position: 'Staff', salary: '$0.00', totalShifts: 0, totalHours: 0, breakTimes: 0, totalEarnings: '$200' },
    { id: '4', name: 'Johan', position: 'Owner', salary: '$0.00', totalShifts: 0, totalHours: 0, breakTimes: 0, totalEarnings: '$30' },
];


const calculateTotals = (data, columns) => {
    if (!data.length) return [];

    const totalHours = {};
    let totalPay = 0;

    data.forEach(row => {
        // Sum up daily hours
        columns.forEach(col => {
            const date = col.id;
            if (row.dailyHours[date]) {
                const [hours, minutes, seconds] = row.dailyHours[date].split(':').map(Number);
                const hoursDecimal = hours + (minutes / 60) + (seconds / 3600);
                totalHours[date] = (totalHours[date] || 0) + hoursDecimal;
            }
        });

        // Sum up total pay
        totalPay += parseFloat(row.totalPay.replace(/[^0-9.-]+/g, ''));
    });

    return [
        t("total"),
        '',
        ...columns.map(col => formatHoursFromDecimal(totalHours[col.id] || 0)),
        formatHoursFromDecimal(Object.values(totalHours).reduce((a, b) => a + b, 0)),
        '',
        formatCurrency(totalPay)
    ];
};

const convertTimeToHours = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours + (minutes / 60);
};

const formatHours = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}`;
};

const formatTotalHours = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}`;
};
const formatSecondsToTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};
const formatDate = date => date.toLocaleDateString('en-US');
const parseDate = input => new Date(...input.split("-").map((v, i) => i === 1 ? v - 1 : v));
const isDate = date => !isNaN(new Date(date).getDate());
const isValidDate = date => date.length === 10 && /^\d{4}-\d{1,2}-\d{1,2}/.test(date) && isDate(date);

// Parse a 'YYYY-MM-DD' API date string as LOCAL midnight. Bare new Date('2026-07-31')
// parses as UTC, which then renders as the previous day in negative-offset
// timezones (same fix as the activity log rows).
const parseApiDate = value => new Date(typeof value === 'string' && value.length === 10 ? `${value}T00:00:00` : value);

/** First letters of the first two words, for an avatar that says something. */
const initialsOf = (name) => String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

/** True for a duration that represents no recorded time, in either format. */
const isBlankDuration = (value) => value === '00:00:00' || value === '0.00 hours' || !value;

const DateRangePicker = ({ state, setState, mdDown, datePickerRef }) => {
    const { t } = useTranslation();
    const { popoverActive, activeDateRange, inputValues, date, ranges } = state;

    const handleInputValueChange = useCallback((type, value) => {
        setState(prev => {
            const newInputValues = { ...prev.inputValues, [type]: value };
            if (isValidDate(value)) {
                const newDate = parseDate(value);
                const newPeriod = prev.activeDateRange.period && ((type === 'since' && newDate <= prev.activeDateRange.period.until) || (type === 'until' && newDate >= prev.activeDateRange.period.since))
                    ? { since: type === 'since' ? newDate : prev.activeDateRange.period.since, until: type === 'until' ? newDate : prev.activeDateRange.period.until }
                    : { since: newDate, until: newDate };
                return { ...prev, activeDateRange: { ...prev.activeDateRange, period: newPeriod }, inputValues: newInputValues };
            }
            return { ...prev, inputValues: newInputValues };
        });
    }, [setState]);

    const handleInputBlur = useCallback(({ relatedTarget }) => {
        if (!relatedTarget || !datePickerRef.current?.contains(relatedTarget)) {
            setState(prev => ({ ...prev, popoverActive: false }));
        }
    }, [setState, datePickerRef]);

    const handleCalendarChange = useCallback(({ start, end }) => {
        const newDateRange = {
            alias: "custom",
            title: t("custom"),
            period: { since: start, until: end }
        };
        setState(prev => ({
            ...prev,
            activeDateRange: newDateRange,
            inputValues: {
                since: formatDate(newDateRange.period.since),
                until: formatDate(newDateRange.period.until),
            }
        }));
    }, [setState]);

    const handleApply = useCallback(() => {
        setState(prev => ({ ...prev, appliedDateRange: prev.activeDateRange, popoverActive: false }));
    }, [setState]);

    const buttonValue = activeDateRange?.title === t("custom") && activeDateRange?.period
        ? `${activeDateRange.period.since.toDateString()} - ${activeDateRange.period.until.toDateString()}`
        : activeDateRange?.title;

    const DailyActivitySelected = activeDateRange?.period
        ? {
            start: activeDateRange.period.since,
            end: activeDateRange.period.until
        }
        : undefined;

    const dateRangeOptions = useMemo(() => ranges.map(range => ({
        label: range.title,
        value: range.alias
    })), [ranges]);

    return (
        <Popover
            autofocusTarget='none'
            fluidContent
            sectioned={false}
            fullHeight
            preferredPosition="below"
            preferredAlignment="left"
            active={popoverActive}
            onClose={() => setState(prev => ({ ...prev, popoverActive: false }))}
            activator={
                <Button
                    icon={CalendarIcon}
                    onClick={() => setState(prev => ({ ...prev, popoverActive: !prev.popoverActive }))}
                >
                    {buttonValue}
                </Button>
            }
        >
            <Popover.Pane fixed>
                <InlineGrid columns={["1fr", "1fr"]} gap={0} ref={datePickerRef}>
                    <div className='flex'>
                        <div>
                            <Box maxWidth='212px' width='100%' style={{ paddingRight: '20px' }}>
                                <Scrollable style={{ height: 'auto' }}>
                                    {mdDown ? (
                                        <Select
                                            label={t("date_range")}
                                            labelHidden
                                            onChange={value => {
                                                const newRange = ranges.find(range => range.alias === value);
                                                setState(prev => ({
                                                    ...prev,
                                                    activeDateRange: newRange,
                                                    inputValues: {
                                                        since: formatDate(newRange.period.since),
                                                        until: formatDate(newRange.period.until),
                                                    }
                                                }));
                                            }}
                                            value={activeDateRange?.alias || ""}
                                            options={dateRangeOptions}
                                        />
                                    ) : (
                                        <OptionList
                                            options={dateRangeOptions}
                                            selected={[activeDateRange?.alias]}
                                            onChange={value => {
                                                const newRange = ranges.find(range => range.alias === value[0]);
                                                setState(prev => ({
                                                    ...prev,
                                                    activeDateRange: newRange,
                                                    inputValues: {
                                                        since: formatDate(newRange.period.since),
                                                        until: formatDate(newRange.period.until),
                                                    }
                                                }));
                                            }}
                                        />
                                    )}
                                </Scrollable>
                            </Box>
                        </div>
                        <div className='w-[500px] p-2'>
                            <Box padding={0} style={{ overflow: 'hidden' }}>
                                <BlockStack gap="400">
                                    <InlineStack gap="200">
                                        <div style={{ flexGrow: 1 }}>
                                            <TextField
                                                role="combobox"
                                                label={t("since")}
                                                labelHidden
                                                prefix={<Icon source={CalendarIcon} />}
                                                value={inputValues.since || ''}
                                                onChange={value => handleInputValueChange('since', value)}
                                                onBlur={handleInputBlur}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <Icon source={ArrowRightIcon} />
                                        <div style={{ flexGrow: 1 }}>
                                            <TextField
                                                role="combobox"
                                                label={t("until")}
                                                labelHidden
                                                prefix={<Icon source={CalendarIcon} />}
                                                value={inputValues.until || ''}
                                                onChange={value => handleInputValueChange('until', value)}
                                                onBlur={handleInputBlur}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </InlineStack>

                                    <DatePicker
                                        month={date.month}
                                        year={date.year}
                                        selected={DailyActivitySelected}
                                        onMonthChange={(month, year) => setState(prev => ({ ...prev, date: { month, year } }))}
                                        onChange={handleCalendarChange}
                                        multiMonth={true}
                                        allowRange
                                        style={{ width: '100%' }}
                                    />
                                </BlockStack>
                            </Box>
                        </div>
                    </div>
                </InlineGrid>
            </Popover.Pane>
            <Popover.Section>
                <InlineStack align="end" gap={200}>
                    <Button onClick={() => setState(prev => ({ ...prev, popoverActive: false }))}>
                        {t("cancel")}
                    </Button>
                    <Button variant='primary' onClick={handleApply}>
                        {t("apply")}
                    </Button>
                </InlineStack>
            </Popover.Section>
        </Popover>
    );
};

// Formats number array to expected format from polaris-viz chart
const formatChartData = (values = []) => {
    return [{ data: values?.map((stat, idx) => ({ key: idx, value: stat })) }];
};

// Gets rate of change based on first + last entry in chart data
const getPercentageChange = (start = 0, end = 0) => {
    if (isNaN(start) || isNaN(end)) return null;

    const percentage = (((end - start) / start) * 100).toFixed(0);

    if (percentage > 999) {
        return 999;
    }

    if (percentage < -999) {
        return -999;
    }

    return percentage;
};

// Update the handleExport function
const handleExport = async (data, columns, type = 'daily-activity') => {
    try {
        const response = await fetch('/api/export-reports', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                data,
                columns,
                type
            }),
        });

        if (!response.ok) throw new Error('Export failed');

        // Get the blob from the response
        const blob = await response.blob();

        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link element
        const link = document.createElement('a');
        link.href = url;
        link.download = `${type}-report-${new Date().toISOString().split('T')[0]}.xlsx`;

        // Append link to body, click it, and remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL
        window.URL.revokeObjectURL(url);
    } catch (error) {
        // Error handled silently
    }
};

/**
 * A single headline figure. `note` is optional context and must be DERIVED from
 * data we actually hold. The note row keeps its height either way so a row of
 * tiles stays level.
 */
const StatTile = ({ icon, tone, label, value, note, loading }) => (
    <div style={{ ...SURFACE, padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <IconDisc icon={icon} tone={tone} />
            <div style={{ minWidth: 0, flex: 1 }}>
                <Text as="span" variant="bodySm" tone="subdued">{label}</Text>
                {loading ? (
                    <div style={{ width: '56px', height: '28px', marginTop: '6px', background: '#f1f1f1', borderRadius: '4px' }} />
                ) : (
                    <div style={{ marginTop: '2px', fontSize: '24px', fontWeight: 700, color: '#303030', letterSpacing: '-0.4px', lineHeight: 1.15 }}>
                        {value}
                    </div>
                )}
                <div style={{ minHeight: '16px', marginTop: '4px' }}>
                    {!loading && note ? (
                        <Text as="span" variant="bodySm" tone="subdued">{note}</Text>
                    ) : null}
                </div>
            </div>
        </div>
    </div>
);

/**
 * Hairlines between StatCard cells, drawn per cell instead of as a 1px grid gap
 * over a grey ground: the gap technique paints that grey into every grid area no
 * cell covers, and both groups hold an odd number of cells (3 and 5), so below
 * the `md` column switch the trailing slot rendered as a solid grey half-cell
 * running into the card's rounded corner. A shadow can only be cast by a cell
 * that exists, so this self-limits; rules falling outside are clipped by SURFACE.
 */
const CELL_RULE = '-1px 0 0 #ebebeb, 0 -1px 0 #ebebeb';

/** A group of related figures as one card: header band, then hairline-split cells. */
const StatCard = ({ icon, tone, title, stats, loading, columns }) => (
    <div style={{ ...SURFACE, height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 16px', borderBottom: '1px solid #ebebeb' }}>
            <IconDisc icon={icon} tone={tone} size={26} />
            <Text as="h3" variant="headingSm">{title}</Text>
        </div>

        <div className={`grid grid-cols-1 ${columns}`} style={{ background: '#ffffff' }}>
            {stats.map((stat) => (
                <div key={stat.key} style={{ background: '#ffffff', padding: '14px 16px', minWidth: 0, boxShadow: CELL_RULE }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ display: 'inline-flex', width: 14, height: 14, alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                            <Icon source={stat.icon} tone="subdued" />
                        </span>
                        <Text as="span" variant="bodySm" tone="subdued">{stat.label}</Text>
                    </div>
                    {loading ? (
                        <div style={{ width: '72px', height: '24px', marginTop: '8px', background: '#f1f1f1', borderRadius: '4px' }} />
                    ) : (
                        <div style={{ marginTop: '6px', fontSize: '20px', fontWeight: 700, color: '#303030', letterSpacing: '-0.3px', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
                            {stat.value}
                        </div>
                    )}
                </div>
            ))}
        </div>
    </div>
);

const Reports = () => {
    const { t } = useTranslation();
    const breakpoints = useBreakpoints();
    const isMobile = breakpoints.smDown;           // ⬅︎ NEW
    const { mdDown, lgDown } = breakpoints;
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const datePickerRef = useRef(null);
    const axiosClient = useAxiosClient();
    const [loading, setLoading] = useState(true);
    const [overviewStats, setOverviewStats] = useState({});
    // Did the overview request actually answer? overviewStats is {} both before
    // the first response and after a failed one, and every figure below coerces a
    // missing field to 0 - so without this flag a dropped request paints a full
    // page of zeros and then narrates them as fact.
    const [statsLoaded, setStatsLoaded] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [dailyActivityData, setDailyActivityData] = useState([]);
    const [hoursFormat, setHoursFormat] = useState('standard');
    const [staffActivity, setStaffActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);
    const [activityPerPage, setActivityPerPage] = useState(50);
    const [activityPagination, setActivityPagination] = useState({
        current_page: 1,
        per_page: 50,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
    });
    const [positionFilter, setPositionFilter] = useState('all');
    const [AvatarStaffIndex, setAvatarStaffIndex] = useState(10);
    const { currencyData } = window.appGlobals || { currencyData: {} };

    // Add skeleton loading animation styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes shimmer {
                0% { opacity: 0.6; }
                50% { opacity: 1; }
                100% { opacity: 0.6; }
            }
            .skeleton-loader {
                animation: shimmer 1.5s ease-in-out infinite;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);
    const getFilteredDailyActivityData = useCallback(() => {
        if (positionFilter === 'all') {
            return dailyActivityData; // Return all data if no filter is applied
        }

        // Filter the data based on the selected position
        return dailyActivityData.filter(row =>
            row.position.toLowerCase() === positionFilter.toLowerCase()
        );
    }, [dailyActivityData, positionFilter]);

    const generateDataPoints = (currentValue) => {
        // If the value is a time string, convert it to hours for chart
        if (typeof currentValue === 'string' && currentValue.includes(':')) {
            const [hours, minutes, seconds] = currentValue.split(':').map(Number);
            currentValue = hours + (minutes / 60) + (seconds / 3600);
        }

        // Handle zero or undefined values
        if (!currentValue || currentValue === 0) {
            return Array(7).fill(0);
        }

        const numberOfPoints = 7;
        const minVariation = 0.85;
        const maxVariation = 1.15;

        return Array.from({ length: numberOfPoints }, (_, index) => {
            if (index === numberOfPoints - 1) return currentValue;

            const randomFactor = minVariation + (Math.random() * (maxVariation - minVariation));
            const value = Math.round(currentValue * randomFactor);
            return Math.max(0, value);
        }).sort((a, b) => a - b);
    };

    const [state, setState] = useState(() => {
        const today = new Date(new Date().setHours(0, 0, 0, 0));
        const yesterday = new Date(new Date(today).setDate(today.getDate() - 1));

        const ranges = [
            {
                title: t("today"),
                alias: "today",
                period: { since: today, until: today }
            },
            {
                title: t("yesterday"),
                alias: "yesterday",
                period: { since: yesterday, until: yesterday }
            },
            {
                title: t("last_7_days"),
                alias: "last7days",
                period: {
                    since: new Date(new Date().setDate(today.getDate() - 7)),
                    until: today
                }
            },
            {
                title: t("last_30_days"),
                alias: "last30days",
                period: {
                    since: new Date(new Date().setDate(today.getDate() - 30)),
                    until: today
                }
            },
            {
                title: t("last_90_days"),
                alias: "last90days",
                period: {
                    since: new Date(new Date().setDate(today.getDate() - 90)),
                    until: today
                }
            },
            {
                title: t("last_365_days"),
                alias: "last365days",
                period: {
                    since: new Date(new Date().setDate(today.getDate() - 365)),
                    until: today
                }
            },
            {
                title: t("custom"),
                alias: "custom",
                period: { since: today, until: today }
            },
        ];

        const last30DaysRange = ranges.find(range => range.alias === "last30days");

        return {
            popoverActive: false,
            activeDateRange: last30DaysRange,
            appliedDateRange: last30DaysRange,
            inputValues: {
                since: formatDate(last30DaysRange.period.since),
                until: formatDate(last30DaysRange.period.until),
            },
            date: {
                month: today.getMonth(),
                year: today.getFullYear(),
            },
            ranges,
            selectedStaff: null,
            selectedStaffId: null,
            selectedAvatarName: '',
            selectedAvatarInitials: '',
            AvatarPopover: false,
        };
    });

    const { appliedDateRange } = state;

    const generateColumns = useCallback(() => {
        if (!appliedDateRange?.period) return [];

        // Get all unique dates from the daily activity data
        const uniqueDates = new Set();
        dailyActivityData.forEach(staff => {
            Object.keys(staff.dailyHours).forEach(date => {
                uniqueDates.add(date);
            });
        });

        // Convert Set to Array and sort dates
        const sortedDates = Array.from(uniqueDates).sort();

        // Generate columns only for dates that exist in the data
        return sortedDates.map(date => {
            const dateObj = new Date(date);
            return {
                id: date,
                title: dateObj.toLocaleDateString('en-US', { weekday: 'short' }),
                subtitle: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            };
        });
    }, [appliedDateRange, dailyActivityData]);

    const OverviewTabs = useMemo(() => [
        {
            id: 'all-customers',
            content: 'All',
            accessibilityLabel: 'All customers',
            panelID: 'all-customers-content',
        },
        {
            id: 'accepts-marketing',
            content: 'Active',
            panelID: 'accepts-marketing-content',
        },
        {
            id: 'repeat-customers',
            content: 'Inactive',
            panelID: 'repeat-customers-content',
        },
    ], []);

    const { mode, setMode } = useSetIndexFiltersMode();
    const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(tableData);
    const fetchStaffActivity = useCallback(async (page = 1, perPage = activityPerPage) => {
        if (!appliedDateRange?.period) return;
        try {
            setActivityLoading(true);
            const startDate = appliedDateRange.period.since;
            const endDate = appliedDateRange.period.until;
            const response = await axiosClient.get('/staff-activity', {
                params: {
                    start_date: startDate.toLocaleDateString('en-US'),
                    end_date: endDate.toLocaleDateString('en-US'),
                    staff_id: state.selectedStaffId,
                    page,
                    per_page: perPage,
                },
            });
            setStaffActivity(response.data.data.activities || []);
            setActivityPagination(response.data.data.pagination || {
                current_page: 1, per_page: perPage, total: 0, last_page: 1, from: 0, to: 0,
            });
        } catch (error) {
            // Error handled silently
        } finally {
            setActivityLoading(false);
        }
    }, [appliedDateRange, state.selectedStaffId, axiosClient, activityPerPage]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const startDate = appliedDateRange.period.since;
            const endDate = appliedDateRange.period.until;

            const [
                overviewResponse,
                staffListResponse,
                dailyActivityResponse,
            ] = await Promise.all([
                axiosClient.get('/overview', {
                    params: { start_date: startDate.toLocaleDateString('en-US'), end_date: endDate.toLocaleDateString('en-US') }
                }),
                axiosClient.get('/staff-list', {
                    params: { start_date: startDate.toLocaleDateString('en-US'), end_date: endDate.toLocaleDateString('en-US') }
                }),
                axiosClient.get('/daily-activity', {
                    params: { start_date: startDate.toLocaleDateString('en-US'), end_date: endDate.toLocaleDateString('en-US') }
                }),
            ]);

            setOverviewStats(overviewResponse.data.data);
            setStatsLoaded(true);
            setStaffList(staffListResponse.data.data);

            if (dailyActivityResponse.data.data) {
                const format = dailyActivityResponse.data.hours_format || 'standard';
                setHoursFormat(format);
                const processedData = processDailyActivityData(dailyActivityResponse.data.data, format);
                setDailyActivityData(processedData);
            }
        } catch (error) {
            // Error handled silently
        } finally {
            setLoading(false);
        }
    };
    const activeStaffCount = Number(overviewStats.active_staffs) || 0;
    const totalStaffCount = Number(overviewStats.total_staffs) || 0;
    const activePercent = totalStaffCount > 0 ? Math.round((activeStaffCount / totalStaffCount) * 100) : null;
    const paidLeaveCount = Number(overviewStats.paid_leaves) || 0;
    const unpaidLeaveCount = Number(overviewStats.unpaid_leaves) || 0;
    const leaveNote = (Number(overviewStats.total_leaves) || 0) === 0
        ? t("reports_note_no_leaves")
        : t("reports_note_leaves_split", { paid: paidLeaveCount, unpaid: unpaidLeaveCount });

    const formatCurrency = (value) => {
        return (currencyData[overviewStats?.currency]?.symbol || '') +
            Number(value).toFixed(2);
    };

    const processDailyActivityData = (data, currentFormat = hoursFormat) => {
        // Create a map to store staff data
        const staffMap = new Map();

        // Get all dates and sort them
        const dates = Object.keys(data).sort();

        // Initialize staff data structure
        dates.forEach(date => {
            const staffEntries = data[date];
            staffEntries.forEach(entry => {
                const staffId = entry.staff_id.toString();
                const staffName = entry.staff_name;

                if (!staffMap.has(staffId)) {
                    staffMap.set(staffId, {
                        id: staffId,
                        name: staffName,
                        position: entry.position,
                        dailyHours: {},
                        totalHours: 0,
                        payroll_type: entry.payroll_type,
                        salary_rate: entry.salary_rate,
                        salaryType: entry.payroll_type === 'Hourly' ? `${(entry.currency) + (entry.salary_rate)}/hr` :
                            entry.payroll_type === 'Weekly' ? `${(entry.currency) + (entry.salary_rate)}/week` :
                                entry.payroll_type === 'Monthly' ? `${(entry.currency) + (entry.salary_rate)}/month` : 'N/A',
                        totalPay: 0,
                        addSalaryEnabled: entry.addSalaryEnabled || false,
                        totalShifts: 0,
                        breakTimes: 0
                    });
                }

                const staff = staffMap.get(staffId);

                // Store the daily hours for this specific date (even if 00:00:00)
                staff.dailyHours[date] = entry.total_time || "00:00:00";

                // Count this as a shift if there's any time recorded
                if (entry.total_time && entry.total_time !== "00:00:00") {
                    staff.totalShifts++;

                    // Calculate total hours
                    const [hours, minutes, seconds] = entry.total_time.split(':').map(Number);
                    const totalHoursDecimal = hours + (minutes / 60) + (seconds / 3600);
                    staff.totalHours += totalHoursDecimal;

                    // Calculate pay based on payroll type
                    if (entry.payroll_type === 'Hourly') {
                        // For hourly staff, calculate based on hours worked
                        staff.totalPay += totalHoursDecimal * parseFloat(entry.salary_rate);
                    } else if (entry.addSalaryEnabled) {
                        // If addSalaryEnabled is true, use the total_pay from the API
                        if (typeof entry.total_pay === 'number' || typeof entry.total_pay === 'string') {
                            const totalPay = parseFloat(entry.total_pay);
                            if (!isNaN(totalPay)) {
                                staff.totalPay = totalPay;
                            }
                        }
                    }
                }

                if (entry.total_break_time) {
                    const [hours, minutes, seconds] = entry.total_break_time.split(':').map(Number);
                    const totalBreakDecimal = hours + (minutes / 60) + (seconds / 3600);
                    staff.breakTimes += totalBreakDecimal;
                }
            });
        });

        // Convert map to array and format the data
        return Array.from(staffMap.values()).map(staff => ({
            ...staff,
            totalHours: formatHoursFromDecimal(staff.totalHours, currentFormat),
            workingHours: formatHoursFromDecimal(Math.max(0, staff.totalHours - staff.breakTimes), currentFormat),
            totalPay: formatCurrency(staff.totalPay)
        }));
    };

    // Tax columns appear only once tax is configured on at least one staff
    // member, leaving the report unchanged for merchants who do not use it.
    const showTaxColumns = useMemo(
        () => staffList.some((s) => s && s.tax_type),
        [staffList],
    );

    const rowMarkup = useMemo(() => staffList.map(
        ({
            id, first_name, last_name, position, salary_amount,
            total_hours, working_hours, total_earnings, total_paid, currency,
            tax_type, tax_deduction, net_earnings,
        }) => (
            <IndexTable.Row id={id} key={id} selected={selectedResources.includes(id)}>
                <IndexTable.Cell>
                    <Box className="flex items-center gap-2">
                        <Avatar source={`https://ui-avatars.com/api/?name=${first_name}+${last_name}`} />
                        <Box paddingLeft="2">{`${first_name} ${last_name}`}</Box>
                    </Box>
                </IndexTable.Cell>
                <IndexTable.Cell>{position}</IndexTable.Cell>
                <IndexTable.Cell>{currency}{salary_amount}</IndexTable.Cell>
                <IndexTable.Cell>{total_hours}</IndexTable.Cell>
                <IndexTable.Cell>{working_hours}</IndexTable.Cell>
                <IndexTable.Cell>{currency}{total_earnings}</IndexTable.Cell>
                {showTaxColumns && (
                    <IndexTable.Cell>{currency}{tax_deduction || '0.00'}</IndexTable.Cell>
                )}
                {showTaxColumns && (
                    <IndexTable.Cell>
                        <Text variant="bodyMd" fontWeight={tax_type ? 'bold' : 'regular'}>
                            {currency}{net_earnings ?? total_earnings}
                        </Text>
                    </IndexTable.Cell>
                )}
                <IndexTable.Cell>{currency}{total_paid}</IndexTable.Cell>
            </IndexTable.Row>
        ),
    ), [selectedResources, staffList]);

    /* ---------- Mobile renderer (NEW) ---------- */
    const renderMobileItem = useCallback((item) => {
        const {
            id, first_name, last_name, position,
            salary_amount, total_hours, total_earnings, total_paid, currency,
        } = item;

        return (
            <ResourceList.Item id={id} accessibilityLabel={`View details for ${first_name} ${last_name}`}>
                <Box className="flex items-start justify-between gap-4">
                    <Box className="flex items-center gap-2">
                        <Avatar
                            size="sm"
                            source={`https://ui-avatars.com/api/?name=${first_name}+${last_name}`}
                        />
                        <Box>
                            <Text variant="bodyMd" fontWeight="bold">{first_name} {last_name}</Text>
                            <Badge tone={position === 'Manager' ? 'success'
                                : position === 'Owner' ? 'attention' : 'info'}
                            >
                                {position}
                            </Badge>
                        </Box>
                    </Box>

                    <Box textAlign="right">
                        <Text variant="bodySm">{currency}{salary_amount}</Text>
                        <Text variant="bodySm" tone="subdued">{total_hours} {t("hrs")}</Text>
                        <Text variant="bodySm">{t("earn")} {currency}{total_earnings}</Text>
                        <Text variant="bodySm">{t("paid")} {currency}{total_paid}</Text>
                    </Box>
                </Box>
            </ResourceList.Item>
        );
    }, []);

    const renderItem = useCallback((item) => {
        return (
            <ResourceList.Item
                id={item.id}
                onClick={() => handleAvatarClick(item)}
                media={
                    <Avatar
                        size="md"
                        name={item.name}
                        initials={item.initials}
                    />
                }
                accessibilityLabel={`View details for ${item.name}`}
            >
                <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {item.name}
                </Text>
            </ResourceList.Item>
        );
    }, []);

    useEffect(() => {
        if (appliedDateRange?.period) {
            fetchData();
            fetchStaffActivity(1); // Always reset to page 1 on date/staff change
        }
    }, [appliedDateRange, state.selectedStaffId]);



    // Utility functions
    const formatHoursFromDecimal = (hoursDecimal, currentFormat = hoursFormat) => {
        if (currentFormat === 'decimal') {
            return `${Number(hoursDecimal).toFixed(2)} hours`;
        }
        const hours = Math.floor(hoursDecimal);
        const minutes = Math.floor((hoursDecimal - hours) * 60);
        const seconds = Math.floor(((hoursDecimal - hours) * 60 - minutes) * 60);
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const formatTimeStringToDecimal = (timeStr) => {
        if (!timeStr || timeStr === '00:00:00') return '0.00 hours';
        const parts = timeStr.split(':').map(Number);
        if (parts.length === 3) {
            const decimalHours = parts[0] + (parts[1] / 60) + (parts[2] / 3600);
            return `${decimalHours.toFixed(2)} hours`;
        }
        return timeStr;
    };

    const formatActivityDuration = (durationStr, currentFormat = hoursFormat) => {
        if (!durationStr) return '--:--:--';
        if (currentFormat === 'decimal') {
            return formatTimeStringToDecimal(durationStr);
        }
        return durationStr;
    };



    const calculateTotals = (data, columns) => {
        if (!data.length) return [];

        const totalHours = {};
        let totalPay = 0;
        let totalShifts = 0;

        data.forEach(row => {
            // Sum up daily hours
            columns.forEach(col => {
                const date = col.id;
                if (row.dailyHours[date]) {
                    const [hours, minutes, seconds] = row.dailyHours[date].split(':').map(Number);
                    const hoursDecimal = hours + (minutes / 60) + (seconds / 3600);
                    totalHours[date] = (totalHours[date] || 0) + hoursDecimal;
                }
            });

            // Sum up total pay
            totalPay += parseFloat(row.totalPay.replace(/[^0-9.-]+/g, ''));

            // Sum up total shifts
            totalShifts += row.totalShifts || 0;
        });

        return [
            t("total"),
            '',
            ...columns.map(col => formatHoursFromDecimal(totalHours[col.id] || 0)),
            formatHoursFromDecimal(Object.values(totalHours).reduce((a, b) => a + b, 0)),
            '',
            formatCurrency(totalPay)
        ];
    };

    const formatStaffActivityRows = useCallback((data) => {
        if (!data || !data.length) return [];

        return data.map(activity => [
            // Use the date string directly from API to avoid timezone conversion issues
            new Date(activity.date + 'T00:00:00').toLocaleDateString(),
            <Box className="flex items-center gap-2">
                <Avatar
                    size="small"
                    name={activity.staff_name}
                    initials={activity.staff_name.split(' ').map(n => n[0]).join('')}
                />
                <Text variant="bodyMd">{activity.staff_name}</Text>
            </Box>,
            activity.location || 'N/A',
            <Badge
                tone={activity.status.toLowerCase() === 'clock-in' ? 'success' : 'critical'}
                progress="complete"
            >
                {activity.status}
            </Badge>,
            activity.start_time || 'N/A',
            activity.end_time || 'Active',
            // Check if the status is 'break' and use total_break_time, otherwise use total_time
            activity.status.toLowerCase() === 'break'
                ? formatActivityDuration(activity.total_break_time)
                : formatActivityDuration(activity.total_time)
        ]);
    }, [hoursFormat]);

    // Always 2, not lgDown ? 2 : 0: the daily-activity grid is one column per day in
    // the selected range, so it overflows at ANY width. Unpinning the name and position
    // on wide screens meant scrolling right lost every row's identity.
    const fixedFirstColumns = 2;

    const StaffActivityRows = useMemo(() => formatStaffActivityRows(staffActivity), [staffActivity, formatStaffActivityRows]);

    const staffMembers = useMemo(() => [
        { id: '1', name: 'John Doe', initials: 'JD' },
        { id: '2', name: 'Jane Smith', initials: 'JS' },
        { id: '3', name: 'Alice Johnson', initials: 'AJ' },
    ], []);

    const handleStaffChange = useCallback((value) => {
        const selectedMember = staffMembers.find((member) => member.id === value);
        setState(prev => ({ ...prev, selectedStaff: selectedMember }));
    }, [staffMembers]);

    const formattedStaffList = useMemo(() => {
        if (!staffList.length) return [{ name: t("select_staff_member"), id: null }];

        return [
            { name: t("select_staff_member"), id: null },
            ...staffList.map(staff => ({
                id: staff.id,
                name: `${staff.first_name} ${staff.last_name}`,
                initials: `${staff.first_name[0]}${staff.last_name[0]}`
            }))
        ];
    }, [staffList]);

    const toggleAvatarPopover = useCallback(
        () => setState(prev => ({ ...prev, AvatarPopover: !prev.AvatarPopover })),
        []
    );

    const handleAvatar = useCallback(() => {
        const totalItems = formattedStaffList.length;
        const interval = AvatarStaffIndex + 3 < totalItems ? 3 : totalItems - AvatarStaffIndex;

        if (interval > 0) {
            setAvatarStaffIndex(prev => prev + interval);
        }
    }, [formattedStaffList.length, AvatarStaffIndex]);

    const handleAvatarClick = useCallback((staffData) => {
        setState(prev => ({
            ...prev,
            selectedAvatarName: staffData.name,
            selectedAvatarInitials: staffData.initials,
            selectedStaffId: staffData.id,
            AvatarPopover: false
        }));
    }, []);

    const handleClearSelection = useCallback(() => {
        setState(prev => ({
            ...prev,
            selectedAvatarName: '',
            selectedAvatarInitials: '',
            selectedStaffId: null,
            AvatarPopover: false
        }));
    }, []);

    const AvatarActivator = (
        <Button onClick={toggleAvatarPopover} disclosure>
            {state.selectedStaffId ? (
                <Box className="flex gap-2 items-center">
                    <Avatar
                        size='xs'
                        name={state.selectedAvatarName}
                        initials={state.selectedAvatarInitials}
                    />
                    <span>{state.selectedAvatarName}</span>
                </Box>
            ) : (
                <Box className="flex gap-4 items-center">
                    <Avatar
                        size="xs"
                        name={t("select_staff_member")}
                    />
                    <span>{t("select_staff_member")}</span>
                </Box>
            )}
        </Button>
    );

    const AvatarStaffList = useMemo(() =>
        formattedStaffList.slice(0, AvatarStaffIndex),
        [formattedStaffList, AvatarStaffIndex]);

    const tabs = useMemo(() => [
        { id: 'overview', label: t("overview"), icon: ChartVerticalIcon },
        { id: 'daily-activity', label: t("daily_activity_report"), icon: CalendarIcon },
        { id: 'staff-activity', label: t("staff_activity_log"), icon: ListBulletedIcon },
    ], [t]);

    return (
        <Page fullWidth>
            {/* Page header - title and the product wordmark, as on Staff. */}
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <Text as="h1" variant="headingLg">{t("reports")}</Text>
                <div style={{ marginTop: '2px' }}>
                  <Text as="p" variant="bodySm" tone="subdued">{t("reports_subtitle")}</Text>
                </div>
              </div>
            </div>


            <div className="m-0 p-0">
                {/* What you are looking at, and the window you are looking at it
                    through, on one line - as on the Shopify Reports page. The
                    picker used to sit on a row of its own above the tabs, which
                    read as a control belonging to the page rather than to the
                    figures under it, and cost a whole band of vertical space.

                    Polaris <Tabs> only accepts string content, so it cannot show
                    an icon per tab; SegmentedTabs can. */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <SegmentedTabs tabs={tabs} selected={selectedTabIndex} onSelect={setSelectedTabIndex} />
                    <DateRangePicker
                        state={state}
                        setState={setState}
                        mdDown={mdDown}
                        datePickerRef={datePickerRef}
                    />
                </div>

                <div>
                    {tabs.map((tab, index) => (
                        <div key={tab.id} id={`tab-content-${index}`} hidden={index !== selectedTabIndex}>
                            {index === 0 && (
                                <div>
                                    <Box paddingBlock="400">
                                        <Layout>
                                            <Layout.Section>
                                                {!loading && !statsLoaded ? (
                                                    <div className="mb-4">
                                                        <Banner tone="critical">{t("reports_load_failed")}</Banner>
                                                    </div>
                                                ) : null}

                                                {/* Team: four headline figures. Notes are derived from data
                                                    we actually hold - see StatTile. */}
                                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                                    <StatTile
                                                        icon={PersonIcon}
                                                        tone="info"
                                                        label={t("total_staffs")}
                                                        value={overviewStats.total_staffs || 0}
                                                        loading={loading}
                                                    />
                                                    <StatTile
                                                        icon={PersonFilledIcon}
                                                        tone="success"
                                                        label={t("active_staffs")}
                                                        value={activeStaffCount}
                                                        note={!statsLoaded ? null : (activePercent === null ? t("reports_note_no_staff") : t("reports_note_percent_of_team", { percent: activePercent }))}
                                                        loading={loading}
                                                    />
                                                    <StatTile
                                                        icon={AlertCircleIcon}
                                                        tone="warning"
                                                        label={t("total_absents")}
                                                        value={overviewStats.total_absents || 0}
                                                        loading={loading}
                                                    />
                                                    <StatTile
                                                        icon={CalendarIcon}
                                                        tone="magic"
                                                        label={t("total_leaves")}
                                                        value={overviewStats.total_leaves || 0}
                                                        note={statsLoaded ? leaveNote : null}
                                                        loading={loading}
                                                    />
                                                </div>

                                                {/* Time and money, grouped. 3fr/5fr so a cell is the same
                                                    width in both cards. */}
                                                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[3fr_5fr]">
                                                    <StatCard
                                                        icon={ClockIcon}
                                                        tone="info"
                                                        title={t("reports_group_time")}
                                                        loading={loading}
                                                        columns="md:grid-cols-3"
                                                        stats={[
                                                            { key: 'total_hours', label: t("total_hours"), icon: ClockIcon, value: overviewStats.total_hours || '00:00:00' },
                                                            { key: 'working_hours', label: t("working_hours"), icon: ClockIcon, value: overviewStats.working_hours || '00:00:00' },
                                                            { key: 'total_break_time', label: t("total_break_time"), icon: ClockIcon, value: overviewStats.total_break_time || '00:00:00' },
                                                        ]}
                                                    />
                                                    <StatCard
                                                        icon={CashDollarIcon}
                                                        tone="success"
                                                        title={t("reports_group_pay")}
                                                        loading={loading}
                                                        columns="md:grid-cols-4"
                                                        stats={[
                                                            { key: 'total_earnings', label: t("total_earnings"), icon: MoneyIcon, value: formatCurrency(overviewStats.total_earnings || 0) },
                                                            { key: 'total_paid', label: t("total_paid"), icon: WalletIcon, value: formatCurrency(overviewStats.total_paid || 0) },
                                                            { key: 'total_unpaid', label: t("total_unpaid"), icon: DiscountIcon, value: formatCurrency(overviewStats.total_unpaid || 0) },
                                                            { key: 'total_bonus', label: t("total_bonus"), icon: RewardIcon, value: formatCurrency(overviewStats.total_bonus || 0) },
                                                        ]}
                                                    />
                                                </div>
                                            </Layout.Section>
                                        </Layout>
                                    </Box>

                                    <div className="mt-4" style={{ ...SURFACE, overflow: 'visible' }}>
                                        <div style={{ padding: '11px 16px', borderBottom: '1px solid #ebebeb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                            <Text as="h3" variant="headingSm">{t("reports_staff_performance")}</Text>
                                            {/* Gated like the tiles above: staffList is [] until the request
                                                answers, so an ungated count reads "0 staff" on first paint and
                                                on every date-range change. */}
                                            {loading ? null : (
                                                <Text as="span" variant="bodySm" tone="subdued">{t("reports_staff_count", { count: staffList.length })}</Text>
                                            )}
                                        </div>
                                        {/* Plain div, not <Card>: the wrapper above already draws the
                                            surface, and a Polaris Card here would render a second border
                                            inside it. */}
                                        <div style={{ padding: '4px' }}>
                                            <IndexTable
                                                resourceName={{ singular: 'staff', plural: 'staffs' }}
                                                itemCount={staffList.length}
                                                onSelectionChange={handleSelectionChange}
                                                headings={[
                                                    { id: 'name', title: <ColumnHeading icon={PersonIcon}>{t("name")}</ColumnHeading> },
                                                    { id: 'position', title: <ColumnHeading icon={WorkIcon}>{t("position")}</ColumnHeading> },
                                                    { id: 'salary', title: <ColumnHeading icon={MoneyIcon}>{t("salary")}</ColumnHeading> },
                                                    { id: 'total_hours', title: <ColumnHeading icon={ClockIcon}>{t("total_hours")}</ColumnHeading> },
                                                    {
                                                        id: 'working_hours',
                                                        title: (
                                                            <InlineStack gap="100" align="start" blockAlign="center">
                                                                <ColumnHeading icon={ClockIcon}>{t("working_hours")}</ColumnHeading>
                                                                <Tooltip content="Working Hours = Total Hours - Break Time" preferredPosition="above">
                                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Icon source={InfoIcon} tone="base" />
                                                                    </div>
                                                                </Tooltip>
                                                            </InlineStack>
                                                        )
                                                    },
                                                    { id: 'total_earnings', title: <ColumnHeading icon={CashDollarIcon}>{t("total_earnings")}</ColumnHeading> },
                                                    ...(showTaxColumns
                                                        ? [
                                                            { id: 'tax_deduction', title: <ColumnHeading icon={TaxIcon}>{t("tax_deduction")}</ColumnHeading> },
                                                            { id: 'net_earnings', title: <ColumnHeading icon={WalletIcon}>{t("net_earnings")}</ColumnHeading> },
                                                          ]
                                                        : []),
                                                    { id: 'total_paid', title: <ColumnHeading icon={ReceiptIcon}>{t("total_paid")}</ColumnHeading> },
                                                ]}
                                                selectable={false}
                                                /* Force wide table so overflow kicks in */
                                                style={{ minWidth: 820 }}
                                            >
                                                {rowMarkup}
                                            </IndexTable>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {index === 1 && (
                                <Box>
                                    <div className="mt-4" style={{ ...SURFACE, overflow: 'visible' }}>
                                        {/* Header band: what this is, how many rows it holds, and the
                                            controls that shape it - so the card is self-contained
                                            instead of trailing a floating toolbar above it. */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', padding: '10px 16px', borderBottom: '1px solid #ebebeb' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                                <IconDisc icon={CalendarIcon} tone="info" size={26} />
                                                <Text as="h3" variant="headingSm">{t("daily_activity_report")}</Text>
                                                {!loading && (
                                                    <Text as="span" variant="bodySm" tone="subdued">
                                                        {t("reports_showing_staff", { count: getFilteredDailyActivityData().length })}
                                                    </Text>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                <Select
                                                    label={t("position")}
                                                    labelInline
                                                    options={[
                                                        { label: t("all_positions"), value: 'all' },
                                                        { label: t("staff"), value: 'staff' },
                                                        { label: t("manager"), value: 'manager' },
                                                        { label: t("owner"), value: 'owner' },
                                                    ]}
                                                    value={positionFilter}
                                                    onChange={(value) => setPositionFilter(value)}
                                                />
                                                {positionFilter !== 'all' && (
                                                    <Button variant="plain" onClick={() => setPositionFilter('all')}>
                                                        {t("clear_filter")}
                                                    </Button>
                                                )}
                                                <Button
                                                    icon={ExportIcon}
                                                    onClick={() => handleExport(getFilteredDailyActivityData(), generateColumns(), 'daily-activity')}
                                                >
                                                    {t("export")}
                                                </Button>
                                            </div>
                                        </div>

                                        <Box padding="100">
                                            {loading ? (
                                                <Box padding="400" textAlign="center">
                                                    <Text tone="subdued">{t("loading")}</Text>
                                                </Box>
                                            ) : getFilteredDailyActivityData().length === 0 ? (
                                                <Box padding="400">
                                                    <Box
                                                        background="bg-surface-secondary"
                                                        borderRadius="200"
                                                        padding="500"
                                                        textAlign="center"
                                                    >
                                                        <Text variant="headingSm" tone="subdued" as="h3">
                                                            {t("no_records_found")}
                                                        </Text>
                                                        <Box paddingBlockStart="200">
                                                            <Text tone="subdued">
                                                                {positionFilter === 'all'
                                                                    ? t("no_staff_activity_data_available_for_the_selected_date_range")
                                                                    : `${t("no_records_found_for_the_selected_date_range")}`
                                                                }
                                                            </Text>
                                                        </Box>
                                                        {positionFilter !== 'all' && (
                                                            <Box paddingBlockStart="400">
                                                                <Button
                                                                    onClick={() => setPositionFilter('all')}
                                                                    tone="success"
                                                                >
                                                                    {t("view_all_positions")}
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <div style={{ overflowX: 'auto' }}>
                                                    <Box>
                                                        <DataTable
                                                            columnContentTypes={[
                                                                'text',
                                                                'text',
                                                                ...generateColumns().map(() => 'numeric'),
                                                                'numeric',
                                                                'numeric',
                                                                'text',
                                                                'numeric'
                                                            ]}
                                                            headings={[
                                                                <Text variant="headingSm">{t("staff_name")}</Text>,
                                                                <Text variant="headingSm">{t("position")}</Text>,
                                                                ...generateColumns().map(col => (
                                                                    (() => {
                                                                        const day = parseApiDate(col.id);
                                                                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                                                        return (
                                                                            <Box>
                                                                                <Text variant="bodySm" tone="subdued">
                                                                                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                                                                                </Text>
                                                                                {/* Weekends subdued so week boundaries are findable in a 30-column grid. */}
                                                                                <Text variant="headingSm" tone={isWeekend ? 'subdued' : 'base'}>
                                                                                    {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                                                </Text>
                                                                            </Box>
                                                                        );
                                                                    })()
                                                                )),
                                                                <Text variant="headingSm">{t("total_hours")}</Text>,
                                                                <InlineStack gap="100" align="start" blockAlign="center">
                                                                    <Text variant="headingSm">{t("working_hours")}</Text>
                                                                    <Tooltip content="Working Hours = Total Hours - Break Time" preferredPosition="above">
                                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                            <Icon source={InfoIcon} tone="base" />
                                                                        </div>
                                                                    </Tooltip>
                                                                </InlineStack>,
                                                                <Text variant="headingSm">{t("rate")}</Text>,
                                                                // <Text variant="headingSm">Total Pay</Text>
                                                            ]}
                                                            rows={getFilteredDailyActivityData().map(row => [
                                                                <Box className="flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                                                                    <Avatar size="small" name={row.name} initials={initialsOf(row.name)} />
                                                                    <Text variant="bodyMd" fontWeight="semibold">{row.name}</Text>
                                                                </Box>,
                                                                <Badge tone={row.position === 'Staff' ? 'info' : row.position === 'Manager' ? 'success' : 'attention'}>
                                                                    {row.position}
                                                                </Badge>,
                                                                ...generateColumns().map(col => {
                                                                    const hours = row.dailyHours[col.id] || '00:00:00'; // Default to '00:00:00' if no data
                                                                    const displayHours = hoursFormat === 'decimal' ? formatTimeStringToDecimal(hours) : hours;
                                                                    const isZero = displayHours === '00:00:00' || displayHours === '0.00 hours';
                                                                    return (
                                                                        <Text variant="bodyMd" alignment="end" tone={isZero ? 'subdued' : 'base'}>
                                                                            {isZero ? '—' : displayHours}
                                                                        </Text>
                                                                    );
                                                                }),
                                                                <Text variant="bodyMd" alignment="end" fontWeight="semibold" tone={isBlankDuration(row.totalHours) ? 'subdued' : 'base'}>
                                                                    {isBlankDuration(row.totalHours) ? '—' : row.totalHours}
                                                                </Text>,
                                                                <Text variant="bodyMd" alignment="end" fontWeight="semibold" tone={isBlankDuration(row.workingHours) ? 'subdued' : 'base'}>
                                                                    {isBlankDuration(row.workingHours) ? '—' : row.workingHours}
                                                                </Text>,
                                                                <Text variant="bodyMd">{row.salaryType}</Text>,
                                                                // <Text variant="bodyMd" alignment="end" fontWeight="semibold">{row.totalPay}</Text>,
                                                            ])}
                                                            fixedFirstColumns={fixedFirstColumns}
                                                            // totals={calculateTotals(getFilteredDailyActivityData(), generateColumns())}
                                                            totalsName={{
                                                                singular: t("total"),
                                                                plural: t("total"),
                                                            }}
                                                            showTotalsInFooter
                                                            loading={loading}
                                                            increasing
                                                            sortable={[0, 1, -3, -2, -1]} // Make name, position, total hours, working hours and rate columns sortable
                                                        />
                                                    </Box>
                                                </div>
                                            )}
                                        </Box>
                                    </div>
                                </Box>
                            )}
                            {index === 2 && (
                                <Box>
                                    <div className="mt-4" style={{ ...SURFACE, overflow: 'visible' }}>
                                        {/* Header band: title, how many records, and the controls that
                                            shape them - matching the other two tabs. */}
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', padding: '10px 16px', borderBottom: '1px solid #ebebeb' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                                <IconDisc icon={ListBulletedIcon} tone="info" size={26} />
                                                <Text as="h3" variant="headingSm">{t("staff_activity_log")}</Text>
                                                {!activityLoading && (
                                                    <Text as="span" variant="bodySm" tone="subdued">
                                                        {state.selectedStaffId
                                                            ? state.selectedAvatarName
                                                            : t("reports_activity_records", { count: activityPagination.total || 0 })
                                                        }
                                                    </Text>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                <Popover
                                                    sectioned
                                                    active={state.AvatarPopover}
                                                    activator={AvatarActivator}
                                                    onClose={toggleAvatarPopover}
                                                    ariaHaspopup={false}
                                                >
                                                    <Popover.Pane onScrolledToBottom={handleAvatar}>
                                                        <ResourceList items={AvatarStaffList} renderItem={renderItem} />
                                                    </Popover.Pane>
                                                </Popover>
                                                {state.selectedStaffId && (
                                                    <Button variant="plain" onClick={handleClearSelection}>
                                                        {t("clear_selection")}
                                                    </Button>
                                                )}
                                                <Button
                                                    icon={ExportIcon}
                                                    onClick={() => handleExport(staffActivity, null, 'staff-activity')}
                                                >
                                                    {t("export")}
                                                </Button>
                                            </div>
                                        </div>

                                        <Box padding="100">
                                            {activityLoading ? (
                                                <Box padding="400" textAlign="center">
                                                    <Text tone="subdued">{t("loading")}</Text>
                                                </Box>
                                            ) : StaffActivityRows.length === 0 ? (
                                                <Box padding="400">
                                                    <Box
                                                        background="bg-surface-secondary"
                                                        borderRadius="200"
                                                        padding="500"
                                                        textAlign="center"
                                                    >
                                                        <Text variant="headingSm" tone="subdued" as="h3">
                                                            {t("no_activity_records_found")}
                                                        </Text>
                                                        <Box paddingBlockStart="200">
                                                            <Text tone="subdued">
                                                                {state.selectedStaffId
                                                                    ? `${t("no_activity_records_found_for_in_the_selected_date_range")} ${state.selectedAvatarName}`
                                                                    : t("no_staff_activity_records_found_for_the_selected_date_range")
                                                                }
                                                            </Text>
                                                        </Box>
                                                        {state.selectedStaffId && (
                                                            <Box paddingBlockStart="400">
                                                                <Button
                                                                    onClick={handleClearSelection}
                                                                    tone="success"
                                                                >
                                                                    {t("view_all_staff_activity")}
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <div style={{ overflowX: 'auto' }}>
                                                    <Box>
                                                        <DataTable
                                                            columnContentTypes={[
                                                                'text',
                                                                'text',
                                                                'text',
                                                                'text',
                                                                'text',
                                                                'text',
                                                                'text'
                                                            ]}
                                                            headings={[
                                                                <Text variant="headingSm">{t("date")}</Text>,
                                                                <Text variant="headingSm">{t("staff")}</Text>,
                                                                <Text variant="headingSm">{t("location")}</Text>,
                                                                <Text variant="headingSm">{t("status")}</Text>,
                                                                <Text variant="headingSm">{t("start_time")}</Text>,
                                                                <Text variant="headingSm">{t("end_time")}</Text>,
                                                                <Text variant="headingSm">{t("total_time")}</Text>
                                                            ]}
                                                            rows={StaffActivityRows}
                                                            fixedFirstColumns={fixedFirstColumns}
                                                            loading={activityLoading}
                                                            increasing
                                                            sortable={[0, 1, 3, 6]}
                                                        />
                                                    </Box>

                                                    {/* Pagination controls */}
                                                    <Box paddingBlockStart="400">
                                                        <InlineStack align="space-between" blockAlign="center" gap="300">
                                                            {/* Rows per page selector */}
                                                            <InlineStack gap="200" blockAlign="center">
                                                                <Text variant="bodySm" tone="subdued">{t("reports_rows_per_page")}</Text>
                                                                <Select
                                                                    label="Rows per page"
                                                                    labelHidden
                                                                    options={[
                                                                        { label: '10', value: '10' },
                                                                        { label: '25', value: '25' },
                                                                        { label: '50', value: '50' },
                                                                        { label: '100', value: '100' },
                                                                        { label: 'All Records', value: '0' },
                                                                    ]}
                                                                    value={String(activityPerPage)}
                                                                    onChange={(value) => {
                                                                        const newPerPage = parseInt(value, 10);
                                                                        setActivityPerPage(newPerPage);
                                                                        fetchStaffActivity(1, newPerPage);
                                                                    }}
                                                                />
                                                            </InlineStack>

                                                            {/* Page navigation — hidden when showing all records */}
                                                            {activityPerPage !== 0 && (
                                                                <InlineStack gap="300" blockAlign="center">
                                                                    <Button
                                                                        disabled={activityPagination.current_page <= 1 || activityLoading}
                                                                        onClick={() => fetchStaffActivity(activityPagination.current_page - 1)}
                                                                    >
                                                                        ← Previous
                                                                    </Button>
                                                                    <Text variant="bodySm" tone="subdued">
                                                                        Page {activityPagination.current_page} of {activityPagination.last_page}
                                                                    </Text>
                                                                    <Button
                                                                        disabled={activityPagination.current_page >= activityPagination.last_page || activityLoading}
                                                                        onClick={() => fetchStaffActivity(activityPagination.current_page + 1)}
                                                                    >
                                                                        Next →
                                                                    </Button>
                                                                </InlineStack>
                                                            )}
                                                        </InlineStack>
                                                    </Box>
                                                </div>
                                            )}
                                        </Box>
                                    </div>
                                </Box>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Page>
    );
};

export default Reports;